export function getCurrencySymbol(currency?: string): string {
  if (!currency) return '$';
  const upper = currency.toUpperCase();
  const map: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'NGN': '₦',
    'XOF': 'CFA',
    'XAF': 'FCFA',
    'ZAR': 'R',
    'KES': 'KSh',
    'GHS': 'GH₵',
  };
  return map[upper] || upper;
}

const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW', 'VND', 'XOF', 'XAF', 'GNF', 'RWF', 'UGX', 'BIF'];

/** Number of minor-unit fraction digits for a currency (0 for zero-decimal). */
export function currencyFractionDigits(currency: string = 'USD'): number {
  return ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase()) ? 0 : 2;
}

export function minorToMajor(priceMinor: number, currency: string = 'USD'): number {
  if (currencyFractionDigits(currency) === 0) {
    return priceMinor;
  }
  return priceMinor / 100;
}

/**
 * Convert a major-unit amount (what an admin types, e.g. 5000 for ₦5,000) into
 * integer minor units for a given currency. Zero-decimal currencies pass through.
 * Returns an integer-valued string suitable for sending to the backend as
 * `fee_minor`. Rounds to the nearest minor unit to avoid float drift.
 */
export function majorToMinorString(major: number, currency: string = 'USD'): string {
  const factor = currencyFractionDigits(currency) === 0 ? 1 : 100;
  return String(Math.round(major * factor));
}

/**
 * Convert integer minor units (as number or numeric string) back into a
 * major-unit number for editing in a major-unit input.
 */
export function minorStringToMajor(minor: string | number | undefined, currency: string = 'USD'): number {
  const n = typeof minor === 'string' ? Number(minor) : (minor ?? 0);
  if (!Number.isFinite(n)) return 0;
  return minorToMajor(n, currency);
}

/**
 * Format an exact decimal amount STRING (as returned by the backend, e.g.
 * "10000.00" or "3000") into a locale-aware currency string, using the record's
 * currency for the symbol/grouping. This does NO arithmetic — it only parses the
 * backend's exact value for display. Returns the raw string + currency code if
 * Intl cannot format the currency.
 */
export function formatDecimalString(decimal?: string, currency?: string): string {
  if (decimal === undefined || decimal === null || decimal === '') return '';
  const n = Number(decimal);
  const cur = (currency || 'USD').toUpperCase();
  if (!Number.isFinite(n)) return `${decimal}${currency ? ` ${cur}` : ''}`;
  const fractionDigits = decimal.includes('.') ? (decimal.split('.')[1]?.length ?? 0) : 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: Math.max(fractionDigits, 2),
    }).format(n);
  } catch {
    return `${getCurrencySymbol(cur)}${decimal}`;
  }
}

/**
 * Format a minor unit price with locale-aware Intl.NumberFormat.
 * Falls back to legacy price formatting if T-04 fields are missing.
 */
export function formatPrice(priceMinor?: number, currency?: string, legacyPrice?: number): string {
  if (priceMinor !== undefined && currency) {
    const major = minorToMajor(priceMinor, currency);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: major % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(major);
    } catch (e) {
      // Fallback if Intl fails on some obscure currency
      const symbol = getCurrencySymbol(currency);
      return `${symbol}${major.toFixed(2)}`;
    }
  }
  
  // Legacy fallback
  if (legacyPrice !== undefined) {
    return `$${legacyPrice}`;
  }
  
  return '';
}
